import { Component, type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import OffersGroupedComponent, { type OffersGroupedIndexProps } from '@cx-modules/offers-grouped';

import { OffersClickoutGuard } from '../offers-clickout';

// Shared OffersGrouped wrapper (NGF-6505), single source of truth for RR7 atlas and atlas-astro.
// Renders the @cx-modules/offers-grouped module with the app-agnostic pre-configuration RR7 used to
// apply inline: the clickout guard and an ErrorBoundary. Simpler than OffersList (OFFERS_PAGE) — the
// grouped module renders standalone, with no repub filtersThreshold, no data-* analytics attributes
// and no ogm-offers-list wrapper divs. One thing was host-decoupled in the extraction:
//   - The error boundary takes an `onError` callback instead of importing @sentry/react-router (which
//     @cx-web/components cannot depend on). RR7 injects `captureException`; atlas-astro (no Sentry
//     yet) injects a console.error shim so failures still surface in the server log / browser console.
//
// CLICKOUT (differs from OFFERS_PAGE): offers-grouped consumes `clickoutServiceUrl` as-is (it does
// NOT append `/offer/clickout` internally, unlike offers-list), so this module must receive the full
// `${CLICKOUT_URL}/offer/clickout` value — callers pass the getOffersClickoutServiceProps output
// through unchanged, with NO bare-URL override. (OFFERS_PAGE overrides to the bare URL because
// offers-list appends the path itself.)
//
// SUSPENSE: @cx-modules/offers-grouped renders via `useSuspenseQuery`, so a `<Suspense fallback={null}>`
// boundary is required for the RR7 direct-render path (in atlas-astro the island's `withIsland` already
// provides one). Without it, a client cache-miss suspend would bubble past this wrapper. Mirrors the
// shared OffersList / OffersSlim wrappers.

export type OffersGroupedProps = Omit<OffersGroupedIndexProps, 'i18n'> & {
  /**
   * Called when the OffersGrouped subtree throws (including the OffersClickoutGuard's deliberate throw
   * on missing/malformed clickout props). RR7 atlas wires this to Sentry's captureException;
   * atlas-astro (no Sentry yet) wires a console.error shim so the failure still surfaces.
   */
  onError?: (error: unknown) => void;
};

class OffersGroupedErrorBoundary extends Component<
  { children: ReactNode; onError?: (error: unknown) => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError?.(error);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export const OffersGrouped = ({ onError, ...rawProps }: OffersGroupedProps) => {
  const { i18n } = useTranslation();

  const props = {
    ...rawProps,
    i18n,
  } as OffersGroupedIndexProps;

  return (
    <OffersGroupedErrorBoundary onError={onError}>
      <Suspense fallback={null}>
        <OffersClickoutGuard componentName="OffersGrouped" values={props}>
          <OffersGroupedComponent {...props} />
        </OffersClickoutGuard>
      </Suspense>
    </OffersGroupedErrorBoundary>
  );
};

type OffersGroupedContentContractProps = {
  [key: string]: unknown;
  geoTargeting?: boolean | string;
  ocApiKey: string;
  repub: string;
  subdivision?: string;
  visibleOffersCount?: number;
};

export const OffersGroupedContentContractForPageTemplate = ({
  geoTargeting,
  ocApiKey,
  repub,
  subdivision,
  visibleOffersCount,
  ...props
}: OffersGroupedContentContractProps) => {
  return {
    ...props,
    // NGF-6300: apiKey is the pre-resolved per-repub OC key threaded via ComponentLoaderContext.ocApiKey
    // (no ambient global ENV / per-repub cast here — Astro-island safe).
    apiKey: ocApiKey,
    // NGF-6505: the one mapping detail that differs from OFFERS_SLIM — do not drop it.
    initialOffersToShow: visibleOffersCount,
    subdivisionCode: subdivision,
    theme: `oc${repub.toLowerCase()}`,
    repub,
    geoTargeting,
    ...(geoTargeting === false
      ? {
          countryCode: undefined,
          subdivisionCode: undefined,
        }
      : {}),
  };
};
